import { EMPTY_ARRAY, KnownObject } from 'pandora-common';
import React, { useCallback, useId, useMemo, useState, type DependencyList, type ReactElement, type ReactNode } from 'react';
import type { OptionalKeysOf } from 'type-fest';
import type { ZodType } from 'zod';
import type { ChildrenProps } from '../../../common/reactTypes.ts';
import { useRemotelyUpdatedUserInput } from '../../../common/useRemotelyUpdatedUserInput.ts';
import { Checkbox } from '../../../common/userInteraction/checkbox.tsx';
import { NumberInput } from '../../../common/userInteraction/input/numberInput.tsx';
import { Select, type SelectProps } from '../../../common/userInteraction/select/select.tsx';
import { Button } from '../../../components/common/button/button.tsx';
import { Column, Row } from '../../../components/common/container/container.tsx';

export interface SettingDriver<T> {
	currentValue: NoInfer<T | undefined>;
	defaultValue: T;
	onChange: NoInfer<(newValue: T) => void>;
	onReset?: () => void;
}

/**
 * Takes a driver for a settings object and returns a driver for a specific property.
 * Subsetting driver does not support direct reset, as parent might contain other properties.
 * @param parentDriver - Driver for the parent settings object
 * @param property - Property to get driver for
 */
export function useSubsettingDriver<const T extends object, const TSetting extends keyof T>(parentDriver: SettingDriver<T>, property: TSetting): SettingDriver<T[TSetting]> {
	return useMemo((): SettingDriver<T[TSetting]> => ({
		currentValue: parentDriver.currentValue?.[property],
		defaultValue: parentDriver.defaultValue[property],
		onChange(newValue) {
			const result: T = {
				...(parentDriver.currentValue ?? parentDriver.defaultValue),
				[property]: newValue,
			};
			return parentDriver.onChange(result);
		},
	}), [parentDriver, property]);
}

/**
 * Takes a driver for a settings object and returns a driver for a specific optional property.
 * Supports doing reset by deleting the optional property
 * @param parentDriver - Driver for the parent settings object
 * @param property - Property to get driver for, must be optional
 * @param defaultValue - Required value of the optional property (required to be passed explicitly, as parent default might not include the value)
 */
export function useOptionalSubsettingDriver<const T extends object, const TSetting extends OptionalKeysOf<T>>(
	parentDriver: SettingDriver<T>,
	property: TSetting,
	defaultValue: NoInfer<Required<T>[TSetting]>,
): SettingDriver<Required<T>[TSetting]> {
	return useMemo((): SettingDriver<Required<T>[TSetting]> => ({
		currentValue: parentDriver.currentValue?.[property],
		defaultValue,
		onChange(newValue) {
			const result: T = {
				...(parentDriver.currentValue ?? parentDriver.defaultValue),
				[property]: newValue,
			};
			return parentDriver.onChange(result);
		},
		onReset() {
			const result: T = { ...(parentDriver.currentValue ?? parentDriver.defaultValue) };
			delete result[property];
			return parentDriver.onChange(result);
		},
	}), [defaultValue, parentDriver, property]);
}

/**
 * Takes a driver for a settings and creates a new driver with bidirectional mapping.
 * This is useful for changing meaning of special values for passing them to generic inputs (e.g. a number to string and back).
 */
export function useValueMapDriver<const TIn, const TOut>(
	parentDriver: SettingDriver<TIn>,
	forwardMapping: (value: NoInfer<TIn>) => TOut,
	backwardMapping: NoInfer<(value: TOut) => TIn>,
): SettingDriver<TOut> {
	return useMemo((): SettingDriver<TOut> => ({
		currentValue: parentDriver.currentValue !== undefined ? forwardMapping(parentDriver.currentValue) : undefined,
		defaultValue: forwardMapping(parentDriver.defaultValue),
		onChange(newValue) {
			return parentDriver.onChange(backwardMapping(newValue));
		},
		onReset: parentDriver.onReset,
	}), [parentDriver, forwardMapping, backwardMapping]);
}

export function useStateSettingDriver<S>(initialState: S): [value: S, driver: SettingDriver<S>] {
	const [value, setValue] = useState<S>(initialState);

	return [
		value,
		useMemo((): SettingDriver<S> => ({
			currentValue: value,
			defaultValue: initialState,
			onChange: setValue,
			onReset() {
				setValue(initialState);
			},
		}), [initialState, value]),
	];
}

function InvertBoolean(v: boolean): boolean {
	return !v;
}

/**
 * Takes a driver for a boolean and creates an inverse driver (with true and false swapped)
 */
export function useBooleanInvertDriver(
	parentDriver: SettingDriver<boolean>,
): SettingDriver<boolean> {
	return useValueMapDriver(parentDriver, InvertBoolean, InvertBoolean);
}

/**
 * Takes a driver for an enum set and returns a driver for a presence of a given value.
 */
export function useEnumSetMembershipDriver<const T extends string>(
	parentDriver: SettingDriver<readonly T[]>,
	value: NoInfer<T>,
): SettingDriver<boolean> {
	return useMemo((): SettingDriver<boolean> => ({
		currentValue: parentDriver.currentValue !== undefined ? parentDriver.currentValue.includes(value) : undefined,
		defaultValue: parentDriver.defaultValue.includes(value),
		onChange(newValue) {
			let newSet = (parentDriver.currentValue ?? parentDriver.defaultValue);
			if (newValue) {
				if (!newSet.includes(value)) {
					newSet = [...newSet, value];
					return parentDriver.onChange(newSet);
				}
			} else {
				newSet = newSet.filter((v) => v !== value);
			}
			return parentDriver.onChange(newSet.toSorted((a, b) => a.localeCompare(b)));
		},
	}), [parentDriver, value]);
}

export function ToggleSettingInput({ driver, label, disabled, noReset = false, deps = EMPTY_ARRAY, children }: {
	driver: Readonly<SettingDriver<boolean>>;
	label: ReactNode;
	noReset?: boolean;
	disabled?: boolean;
	deps?: DependencyList;
} & ChildrenProps): ReactElement {
	const [value, setValue] = useRemotelyUpdatedUserInput(driver.currentValue, deps, {
		updateCallback(newValue) {
			if (newValue === undefined) {
				if (driver.onReset) {
					driver.onReset();
				} else {
					driver.onChange(driver.defaultValue);
				}
			} else {
				driver.onChange(newValue);
			}
		},
	});

	const id = `setting-${useId()}`;

	return (
		<Row alignX='space-between' alignY='center' gap='medium'>
			<Checkbox
				id={ id }
				checked={ value ?? driver.defaultValue }
				onChange={ setValue }
				disabled={ disabled }
			/>
			<label
				htmlFor={ id }
				className='flex-1'
			>
				{ label }
			</label>
			{ children }
			{
				noReset ? null : (
					<Button
						className='slim'
						onClick={ () => setValue(undefined) }
						disabled={ value === undefined }
					>
						↺
					</Button>
				)
			}
		</Row>
	);
}

type NumberSettingInputProps = ChildrenProps & {
	driver: Readonly<SettingDriver<number>>;
	label: string;
	deps?: DependencyList;
	withSlider?: boolean;
	min?: number;
	max?: number;
	step?: number;
	disabled?: boolean;
	noWrapper?: boolean;
	noReset?: boolean;
};

export function NumberSettingInput({
	driver,
	label,
	deps = EMPTY_ARRAY,
	withSlider = false,
	min, max, step,
	disabled = false,
	noWrapper = false,
	noReset = false,
	children,
}: NumberSettingInputProps): ReactElement {
	const id = `setting-${useId()}`;

	const [value, setValue] = useRemotelyUpdatedUserInput(driver.currentValue, deps, {
		updateCallback(newValue) {
			if (newValue === undefined) {
				if (driver.onReset) {
					driver.onReset();
				} else {
					driver.onChange(driver.defaultValue);
				}
			} else {
				driver.onChange(newValue);
			}
		},
	});

	const Wrapper = useMemo(() => (noWrapper ? React.Fragment : ({ children: wrapperChildren }: ChildrenProps) => <Column gap='small'>{ wrapperChildren }</Column>), [noWrapper]);

	return (
		<Wrapper>
			{ label != null ? (
				<label htmlFor={ id }>{ label }</label>
			) : null }
			<Row alignY='center' gap='medium'>
				{
					withSlider && min != null && max != null ? (
						<NumberInput
							aria-label={ label }
							className='flex-6 zero-width'
							rangeSlider
							min={ min }
							max={ max }
							step={ step }
							value={ value ?? driver.defaultValue }
							onChange={ setValue }
							disabled={ disabled }
						/>
					) : null
				}
				<NumberInput
					id={ id }
					aria-label={ label }
					className='flex-grow-1 value'
					min={ min }
					max={ max }
					step={ step }
					value={ value ?? driver.defaultValue }
					onChange={ setValue }
					disabled={ disabled }
				/>
				{ children }
				{ noReset ? null : (
					<Button
						className='slim'
						onClick={ () => setValue(undefined) }
						disabled={ value === undefined }
					>
						↺
					</Button>
				) }
			</Row>
		</Wrapper>
	);
}

export interface SelectSettingInputProps<TValue extends string> {
	driver: Readonly<SettingDriver<TValue>>;
	label: ReactNode;
	stringify: NoInfer<Readonly<Record<TValue, string | (() => string)>>>;
	optionOrder?: NoInfer<readonly TValue[]>;
	schema: NoInfer<ZodType<TValue>>;
	disabled?: boolean;
	noWrapper?: boolean;
	noReset?: boolean;
	deps?: DependencyList;
	children?: ReactNode;
}

export function SelectSettingInput<TValue extends string>({ driver, label, stringify, optionOrder, schema, disabled, noWrapper = false, noReset = false, deps, children }: SelectSettingInputProps<TValue>): ReactElement {
	const [value, setValue] = useRemotelyUpdatedUserInput<TValue | undefined>(driver.currentValue, deps, {
		updateCallback(newValue) {
			if (newValue === undefined) {
				if (driver.onReset) {
					driver.onReset();
				} else {
					driver.onChange(driver.defaultValue);
				}
			} else {
				driver.onChange(newValue);
			}
		},
	});

	const id = `setting-${useId()}`;

	const onInputChange = useCallback<NonNullable<SelectProps['onChange']>>((e) => {
		const newValue = schema.parse(e.target.value);
		setValue(newValue);
	}, [schema, setValue]);

	const options = useMemo(() => {
		const entries = KnownObject.entries(stringify);

		if (optionOrder != null) {
			entries.sort((a, b) => optionOrder.indexOf(a[0]) - optionOrder.indexOf(b[0]));
		}

		return entries.map(([k, v]) => (
			<option key={ k } value={ k }>
				{ typeof v === 'function' ? v() : v }
			</option>
		));
	}, [stringify, optionOrder]);

	const Wrapper = useMemo(() => (noWrapper ? React.Fragment : ({ children: wrapperChildren }: ChildrenProps) => <div className='input-section'>{ wrapperChildren }</div>), [noWrapper]);

	return (
		<Wrapper>
			{ label != null ? (
				<label htmlFor={ id }>{ label }</label>
			) : null }
			<Row alignY='center'>
				<Select
					id={ id }
					className='flex-1'
					value={ value ?? driver.defaultValue }
					onChange={ onInputChange }
					disabled={ disabled }
				>
					{ options }
				</Select>
				{
					noReset ? null : (
						<Button
							className='slim'
							onClick={ () => setValue(undefined) }
							disabled={ value === undefined }
						>
							↺
						</Button>
					)
				}
				{ children }
			</Row>
		</Wrapper>
	);
}
